import React, { useState, useEffect, useRef } from "react";
import "../../../style/editProductModal.css";
import { useSelector } from "react-redux";

import { Upload, Plus, ChevronDown, Check, X } from "lucide-react";
import { toast } from "react-toastify";
import { brandsAPI, categoryAPI, tagAPI } from "../../../services";

// Helper function to convert variants to the format expected by the edit modal
const convertVariantsToEditFormat = (product) => {
  const variants = [];

  // Handle new API structure where variants are objects with direct properties
  if (product.variants && Array.isArray(product.variants)) {
    product.variants.forEach((variant) => {
      Object.entries(variant).forEach(([key, value]) => {
        if (value) {
          variants.push({
            name: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
            value: value,
          });
        }
      });
    });
  }

  // Handle old structure with name/value pairs (backward compatibility)
  if (
    variants.length === 0 &&
    product.variantFeatures &&
    Array.isArray(product.variantFeatures)
  ) {
    product.variantFeatures.forEach((variant) => {
      if (variant.name && variant.value) {
        variants.push({
          name: variant.name,
          value: variant.value,
        });
      }
    });
  }

  // Handle additional_info as fallback
  if (
    variants.length === 0 &&
    product.additional_info &&
    typeof product.additional_info === "object"
  ) {
    Object.entries(product.additional_info).forEach(([key, value]) => {
      if (value) {
        variants.push({
          name: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
          value: value,
        });
      }
    });
  }

  return variants;
};

const EditProductModal = ({ isOpen, onClose, onSubmit, product }) => {
  const [formData, setFormData] = useState({
    category: "",
    brandName: "",
    modelName: "",
    ram: "",
    suffix: "",
    storage: "",
    isNewDevice: false,
    isUsedDevice: false,
    thumbnailImage: null,
    variantFeatures: [],
    tag: "",
    location: "",
    sellingPrice: "",
  });

  const [variantFeature, setVariantFeature] = useState({
    name: "",
    value: "",
  });

  const [editingVariantIndex, setEditingVariantIndex] = useState(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const { isPartsURL, isAccessoriesURL, isGodownURL, hideProductRamStorage, hideProductNewUsed } =
    useSelector((state) => state.domain);
  const isLoycomDomain =
    window.location.hostname === process.env.REACT_APP_WEB_LOYCOM_URL;
  const isInitialLoad = useRef(true);

  const dropdownRef = useRef(null);
  const brandDropdownRef = useRef(null);
  const tagDropdownRef = useRef(null);

  // Load categories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      // Don't load brands initially - wait for category selection
      // Load tags if this is a godown product
      if (isGodownURL) {
        loadTags();
      }
    }
  }, [isOpen, isGodownURL]);

  // Load brands when category changes
  useEffect(() => {
    if (formData.category) {
      loadBrands(formData.category);
    } else {
      setBrands([]);
      setFormData((prev) => ({
        ...prev,
        brandName: "",
      }));
    }
  }, [formData.category]);

  // Reset brand if it doesn't exist in the loaded brands list (skip on initial load)
  useEffect(() => {
    if (!isInitialLoad.current && brands.length > 0 && formData.brandName) {
      const brandExists = brands.some(
        (brand) => brand.id === parseInt(formData.brandName)
      );
      if (!brandExists) {
        setFormData((prev) => ({
          ...prev,
          brandName: "",
        }));
      }
    }
  }, [brands]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        brandDropdownRef.current &&
        !brandDropdownRef.current.contains(event.target)
      ) {
        setIsBrandDropdownOpen(false);
      }
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setIsTagDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getCategoriesList({ page_size: 100 });
      setCategories(response.results || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Failed to load categories. Using default options.");
      // Fallback to default categories
      setCategories([
        { id: 1, name: "Mobile" },
        { id: 2, name: "Tablet" },
        { id: 3, name: "Laptop" },
        { id: 4, name: "Accessories" },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadBrands = async (categoryId = null) => {
    try {
      setLoadingBrands(true);
      let response;
      if (categoryId) {
        response = await categoryAPI.getBrandsByCategory(categoryId, {
          page_size: 100,
        });
      } else {
        response = await brandsAPI.getBrandsList({ page_size: 100 });
      }
      setBrands(response.results || []);
    } catch (error) {
      console.error("Error loading brands:", error);
      toast.error("Failed to load brands. Using default options.");
      // Fallback to default brands
      setBrands([
        { id: 1, name: "Apple" },
        { id: 2, name: "Samsung" },
        { id: 3, name: "OnePlus" },
        { id: 4, name: "Google" },
      ]);
    } finally {
      setLoadingBrands(false);
    }
  };

  const loadTags = async (search = "") => {
    try {
      setLoadingTags(true);
      const response = await tagAPI.getTagsList({ search, page_size: 100 });
      setTags(response.results || []);
    } catch (error) {
      console.error("Error loading tags:", error);
      toast.error("Failed to load tags.");
      setTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  // Populate form when product changes and categories are loaded
  useEffect(() => {
    if (product && isOpen && categories.length > 0) {
      isInitialLoad.current = true;

      // Handle brand - use brand ID directly if available, otherwise find by name
      let brandValue = product.brand || product.brand_id || "";

      // If brand is a string (name), find the matching brand ID
      if (brands.length > 0 && typeof brandValue === "string") {
        const matchingBrand = brands.find(
          (brand) => brand.name.toLowerCase() === brandValue.toLowerCase()
        );
        if (matchingBrand) {
          brandValue = matchingBrand.id;
        }
      }

      // Handle tag - use unique_identifier (tag ID) if available
      let tagValue = product.unique_identifier || product.tag || "";

      setFormData({
        category: product.category || "",
        brandName: brandValue,
        modelName:
          product.model_name || product.name || product.modelName || "",
        ram: product.ram || "",
        suffix: product.suffix || "",
        storage: product.storage || "",
        isNewDevice: product.used_mobile === false ? true : false,
        isUsedDevice: product.used_mobile === true ? true : false,
        thumbnailImage: product.image || product.thumbnailImage || null,
        variantFeatures: convertVariantsToEditFormat(product),
        tag: tagValue,
        location: product.location || "",
        sellingPrice: product.avg_selling_price || "",
      });

      // Set image preview if product has existing image
      const imageUrl = product.image || product.thumbnailImage;
      if (imageUrl && typeof imageUrl === "string") {
        setImagePreview(imageUrl);
      }

      // Close dropdowns when form is populated
      setIsDropdownOpen(false);
      setIsBrandDropdownOpen(false);

      setTimeout(() => {
        isInitialLoad.current = false;
      }, 100);
    }
  }, [product, isOpen, categories]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checked = e.target.checked;

      // Handle mutually exclusive checkboxes for device condition
      if (name === "isNewDevice" && checked) {
        setFormData((prev) => ({
          ...prev,
          isNewDevice: true,
          isUsedDevice: false,
        }));
      } else if (name === "isUsedDevice" && checked) {
        setFormData((prev) => ({
          ...prev,
          isNewDevice: false,
          isUsedDevice: true,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        thumbnailImage: file,
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      thumbnailImage: null,
    }));
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById("thumbnailImage");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleAddVariant = () => {
    if (variantFeature.name && variantFeature.value) {
      if (editingVariantIndex !== null) {
        // Update existing variant
        setFormData((prev) => ({
          ...prev,
          variantFeatures: prev.variantFeatures.map((variant, index) =>
            index === editingVariantIndex
              ? { name: variantFeature.name, value: variantFeature.value }
              : variant
          ),
        }));
        setEditingVariantIndex(null);
      } else {
        // Add new variant
        setFormData((prev) => ({
          ...prev,
          variantFeatures: [
            ...prev.variantFeatures,
            { name: variantFeature.name, value: variantFeature.value },
          ],
        }));
      }
      setVariantFeature({ name: "", value: "" });
    }
  };

  const handleRemoveVariant = (index) => {
    setFormData((prev) => ({
      ...prev,
      variantFeatures: prev.variantFeatures.filter((_, i) => i !== index),
    }));
  };

  const handleEditVariant = (index) => {
    const variant = formData.variantFeatures[index];
    setVariantFeature({ name: variant.name, value: variant.value });
    setEditingVariantIndex(index);
  };

  const handleCancelEdit = () => {
    setVariantFeature({ name: "", value: "" });
    setEditingVariantIndex(null);
  };

  const handleCategorySelect = (categoryId, categoryName) => {
    if (formData.category !== categoryId) {
      setFormData((prev) => ({
        ...prev,
        category: categoryId,
        brandName: "",
      }));
    }
    setIsDropdownOpen(false);
  };

  const handleBrandSelect = (brandId, brandName) => {
    setFormData((prev) => ({
      ...prev,
      brandName: brandId,
    }));
    setIsBrandDropdownOpen(false);
  };

  const handleTagSelect = (tagId, tagName) => {
    setFormData((prev) => ({
      ...prev,
      tag: tagId,
    }));
    setIsTagDropdownOpen(false);
    setTagSearchQuery("");
  };

  const handleTagDropdownOpen = () => {
    setIsTagDropdownOpen(!isTagDropdownOpen);
    if (tags.length === 0 && !isTagDropdownOpen) {
      loadTags();
    }
  };

  const handleTagSearch = (e) => {
    const value = e.target.value;
    setTagSearchQuery(value);
    loadTags(value);
  };

  const getSelectedCategoryLabel = () => {
    const selected = categories.find(
      (category) => category.id === formData.category
    );
    return selected ? selected.name : formData.category || "Select Category";
  };

  const getSelectedBrandLabel = () => {
    // Handle both ID-based and name-based brand selection
    let selected;

    if (
      typeof formData.brandName === "number" ||
      (typeof formData.brandName === "string" && !isNaN(formData.brandName))
    ) {
      // If brandName is a number (ID), find by ID
      const brandId = parseInt(formData.brandName);
      selected = brands.find((brand) => brand.id === brandId);
    } else if (typeof formData.brandName === "string") {
      // If brandName is a string, find by name (case-insensitive)
      selected = brands.find(
        (brand) => brand.name.toLowerCase() === formData.brandName.toLowerCase()
      );
    }

    return selected ? selected.name : formData.brandName || "Select Brand";
  };

  const getSelectedTagLabel = () => {
    const selected = tags.find((tag) => tag.id === formData.tag);
    if (selected) {
      return selected.identifier;
    }
    // If tag is selected but not in the loaded tags list, show the identifier from product
    if (formData.tag && product && product.unique_identifier_identifier) {
      return product.unique_identifier_identifier;
    }
    return "Select Tag";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Tag UI exists only on godown — omit tag elsewhere so other domains stay unchanged
    const { tag, isNewDevice, isUsedDevice, ...rest } = formData;
    const payload = isGodownURL ? { ...rest, tag: tag || "" } : rest;
    if (!hideProductNewUsed) {
      payload.isNewDevice = isNewDevice;
      payload.isUsedDevice = isUsedDevice;
    }
    onSubmit(payload);
    onClose();
  };

  const handleClose = () => {
    setFormData({
      category: "",
      brandName: "",
      modelName: "",
      ram: "",
      suffix: "",
      storage: "",
      isNewDevice: false,
      isUsedDevice: false,
      thumbnailImage: null,
      variantFeatures: [],
      tag: "",
      location: "",
      sellingPrice: "",
    });
    setVariantFeature({ name: "", value: "" });
    setEditingVariantIndex(null);
    setImagePreview(null);
    setIsDropdownOpen(false);
    setIsBrandDropdownOpen(false);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-product-modal-overlay" onClick={handleOverlayClick}>
      <div className="edit-product-modal-container">
        <div className="edit-product-modal-header">
          <div className="edit-product-modal-title">
            <h2>Edit Product</h2>
            <p>Update the product details below</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="edit-product-modal-form">
          <div className="edit-product-modal-form-grid">
            <div className="edit-product-modal-form-column left-column">
              <div className="edit-product-modal-form-group">
                <label htmlFor="category">Category *</label>
                <div
                  className="edit-product-modal-custom-dropdown"
                  ref={dropdownRef}
                >
                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-dropdown-trigger-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-dropdown-trigger-acc"
                        : isGodownURL
                          ? "add-product-modal-dropdown-trigger-godown"
                          : "add-product-modal-dropdown-trigger"
                      } ${isDropdownOpen ? "open" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                  >
                    <span className="edit-product-modal-dropdown-selected">
                      {getSelectedCategoryLabel()}
                    </span>
                    <ChevronDown
                      className={`edit-product-modal-dropdown-arrow ${isDropdownOpen ? "open" : ""
                        }`}
                    />
                  </div>

                  {isDropdownOpen && (
                    <div
                      className="edit-product-modal-dropdown-menu"
                      style={{ display: "block", visibility: "visible" }}
                    >
                      {loadingCategories ? (
                        <div className="edit-product-modal-dropdown-option">
                          <span>Loading categories...</span>
                        </div>
                      ) : (
                        categories.map((category) => (
                          <div
                            key={category.id}
                            className={`edit-product-modal-dropdown-option ${formData.category === category.id
                              ? "selected"
                              : ""
                              }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleCategorySelect(category.id, category.name);
                            }}
                          >
                            {formData.category === category.id && (
                              <Check className="edit-product-modal-check-icon" />
                            )}
                            <span>{category.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="edit-product-modal-form-group">
                <label htmlFor="brandName">Brand Name *</label>
                <div
                  className="edit-product-modal-custom-dropdown"
                  ref={brandDropdownRef}
                >
                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-dropdown-trigger-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-dropdown-trigger-acc"
                        : isGodownURL
                          ? "add-product-modal-dropdown-trigger-godown"
                          : "add-product-modal-dropdown-trigger"
                      } ${isBrandDropdownOpen ? "open" : ""} ${!formData.category ? "disabled" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (formData.category) {
                        setIsBrandDropdownOpen(!isBrandDropdownOpen);
                      }
                    }}
                    style={{
                      cursor: !formData.category ? "not-allowed" : "pointer",
                      opacity: !formData.category ? 0.5 : 1,
                    }}
                  >
                    <span className="edit-product-modal-dropdown-selected">
                      {!formData.category
                        ? "Select Category First"
                        : getSelectedBrandLabel()}
                    </span>
                    <ChevronDown
                      className={`edit-product-modal-dropdown-arrow ${isBrandDropdownOpen ? "open" : ""
                        }`}
                    />
                  </div>

                  {isBrandDropdownOpen && (
                    <div
                      className="edit-product-modal-dropdown-menu"
                      style={{ display: "block", visibility: "visible" }}
                    >
                      {loadingBrands ? (
                        <div className="edit-product-modal-dropdown-option">
                          <span>Loading brands...</span>
                        </div>
                      ) : brands.length === 0 ? (
                        <div className="edit-product-modal-dropdown-option">
                          <span>No brands found</span>
                        </div>
                      ) : (
                        brands.map((brand) => (
                          <div
                            key={brand.id}
                            className={`edit-product-modal-dropdown-option ${parseInt(formData.brandName) === brand.id
                              ? "selected"
                              : ""
                              }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBrandSelect(brand.id, brand.name);
                            }}
                          >
                            {parseInt(formData.brandName) === brand.id && (
                              <Check className="edit-product-modal-check-icon" />
                            )}
                            <span>{brand.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`${isPartsURL
                  ? "add-product-modal-form-group-parts"
                  : isAccessoriesURL
                    ? "add-product-modal-form-group-acc"
                    : isGodownURL
                      ? "add-product-modal-form-group-godown"
                      : "add-product-modal-form-group"
                  }`}
              >
                <label htmlFor="modelName">{isGodownURL ? "Product Name *" : "Model Name *"}</label>
                <input
                  type="text"
                  id="modelName"
                  name="modelName"
                  placeholder={isGodownURL ? "e.g. Wireless Mouse" : "e.g. iPhone 14 Pro"}
                  value={formData.modelName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {!isGodownURL && !hideProductRamStorage && (
                <div
                  className={`${isPartsURL
                    ? "add-product-modal-form-group-parts"
                    : isAccessoriesURL
                      ? "add-product-modal-form-group-acc"
                      : "add-product-modal-form-group"
                    }`}
                >
                  <label htmlFor="ram">RAM</label>
                  <input
                    type="text"
                    id="ram"
                    name="ram"
                    placeholder="e.g. 8GB"
                    value={formData.ram}
                    onChange={handleInputChange}
                  />
                </div>
              )}
              {isGodownURL && (
                <div
                  className={`${isPartsURL
                    ? "add-product-modal-form-group-parts"
                    : isAccessoriesURL
                      ? "add-product-modal-form-group-acc"
                      : isGodownURL
                        ? "add-product-modal-form-group-godown"
                        : "add-product-modal-form-group"
                    }`}
                >
                  <label htmlFor="suffix">Suffix</label>
                  <input
                    type="text"
                    id="suffix"
                    name="suffix"
                    placeholder="e.g. Pro Max"
                    value={formData.suffix}
                    onChange={handleInputChange}
                  />
                </div>
              )}
            </div>

            <div className={`add-product-modal-form-column ${isGodownURL ? "edit-right-column-godown" : "edit-right-column-product"}`}>
              <div
                className={`${isPartsURL
                  ? "add-product-modal-form-group-parts"
                  : isAccessoriesURL
                    ? "add-product-modal-form-group-acc"
                    : isGodownURL
                      ? "add-product-modal-form-group-godown"
                      : "add-product-modal-form-group"
                  }`}
              >
                <label htmlFor="thumbnailImage">Thumbnail Image</label>
                {imagePreview ? (
                  <div className="edit-product-modal-image-preview-container">
                    <div
                      className={`${isPartsURL
                        ? "add-product-modal-image-preview-parts"
                        : isAccessoriesURL
                          ? "add-product-modal-image-preview-acc"
                          : isGodownURL
                            ? "add-product-modal-image-preview-godown"
                            : "add-product-modal-image-preview"
                        }`}
                    >
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="edit-product-modal-remove-image-btn"
                        onClick={handleRemoveImage}
                      >
                        <X size={16} />
                      </button>
                      <label
                        htmlFor="thumbnailImage"
                        className={`${isPartsURL
                          ? "add-product-modal-change-image-overlay-parts"
                          : isAccessoriesURL
                            ? "add-product-modal-change-image-overlay-acc"
                            : isGodownURL
                              ? "add-product-modal-change-image-overlay-godown"
                              : "add-product-modal-change-image-overlay"
                          }`}
                      >
                        Change Image
                      </label>
                      <input
                        type="file"
                        id="thumbnailImage"
                        name="thumbnailImage"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="edit-product-modal-image-upload-input"
                        style={{ display: "none" }}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-image-upload-area-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-image-upload-area-acc"
                        : isGodownURL
                          ? "add-product-modal-image-upload-area-godown"
                          : isGodownURL
                            ? "add-product-modal-image-upload-area-godown"
                            : "add-product-modal-image-upload-area"
                      }`}
                  >
                    <input
                      type="file"
                      id="thumbnailImage"
                      name="thumbnailImage"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      className="edit-product-modal-image-upload-input"
                    />
                    <div className="edit-product-modal-image-upload-content">
                      <Upload
                        className={`${isPartsURL
                          ? "add-product-modal-upload-icon-parts"
                          : isAccessoriesURL
                            ? "add-product-modal-upload-icon-acc"
                            : isGodownURL
                              ? "add-product-modal-upload-icon-godown"
                              : "add-product-modal-upload-icon"
                          }`}
                      />
                      <p>The image should be JPG, PNG, WEBP format</p>
                    </div>
                  </div>
                )}
              </div>

              {isGodownURL ? (
                <>
                  <div className="edit-product-modal-form-group">
                    <label htmlFor="tag">Tag</label>
                    <div
                      className="edit-product-modal-custom-dropdown"
                      ref={tagDropdownRef}
                    >
                      <div
                        className={`add-product-modal-dropdown-trigger ${isTagDropdownOpen ? "open" : ""
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleTagDropdownOpen();
                        }}
                      >
                        <span className="edit-product-modal-dropdown-selected">
                          {getSelectedTagLabel()}
                        </span>
                        <ChevronDown
                          className={`edit-product-modal-dropdown-arrow ${isTagDropdownOpen ? "open" : ""
                            }`}
                        />
                      </div>

                      {isTagDropdownOpen && (
                        <div
                          className="edit-product-modal-dropdown-menu"
                          style={{ display: "block", visibility: "visible" }}
                        >
                          <div className="edit-product-modal-dropdown-search">
                            <input
                              type="text"
                              placeholder="Search tags..."
                              value={tagSearchQuery}
                              onChange={handleTagSearch}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {loadingTags ? (
                            <div className="edit-product-modal-dropdown-option">
                              <span>Loading tags...</span>
                            </div>
                          ) : (
                            <>
                              <div
                                className={`edit-product-modal-dropdown-option ${!formData.tag ? "selected" : ""}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleTagSelect("", "");
                                }}
                              >
                                {!formData.tag && (
                                  <Check className="edit-product-modal-check-icon" />
                                )}
                                <span>None</span>
                              </div>
                              {tags.length === 0 ? (
                                <div className="edit-product-modal-dropdown-option">
                                  <span>No tags found</span>
                                </div>
                              ) : (
                                tags.map((tag) => (
                                  <div
                                    key={tag.id}
                                    className={`edit-product-modal-dropdown-option ${formData.tag === tag.id ? "selected" : ""
                                      }`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleTagSelect(tag.id, tag.identifier);
                                    }}
                                  >
                                    {formData.tag === tag.id && (
                                      <Check className="edit-product-modal-check-icon" />
                                    )}
                                    <span>{tag.identifier}</span>
                                  </div>
                                ))
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-form-group-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-form-group-acc"
                        : isGodownURL
                          ? "add-product-modal-form-group-godown"
                          : "add-product-modal-form-group"
                      }`}
                  >
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      placeholder="e.g. Warehouse A, Shelf 5"
                      value={formData.location}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              ) : (
                <>

                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-form-group-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-form-group-acc"
                        : isGodownURL
                          ? "add-product-modal-form-group-godown"
                          : "add-product-modal-form-group"
                      }`}
                  >
                    <label htmlFor="suffix">Suffix</label>
                    <input
                      type="text"
                      id="suffix"
                      name="suffix"
                      placeholder="e.g. Pro Max"
                      value={formData.suffix}
                      onChange={handleInputChange}
                    />
                  </div>

                  {!hideProductRamStorage && (
                  <div
                    className={`${isPartsURL
                      ? "add-product-modal-form-group-parts"
                      : isAccessoriesURL
                        ? "add-product-modal-form-group-acc"
                        : "add-product-modal-form-group"
                      }`}
                  >
                    <label htmlFor="storage">Storage</label>
                    <input
                      type="text"
                      id="storage"
                      name="storage"
                      placeholder="e.g. 256GB"
                      value={formData.storage}
                      onChange={handleInputChange}
                    />
                  </div>
                  )}
                </>
              )}
            </div>
          </div>

          {!hideProductNewUsed && (
            <div className="edit-product-modal-device-condition">
              <div
                className={`${isPartsURL
                  ? "add-product-modal-checkbox-group-parts"
                  : isAccessoriesURL
                    ? "add-product-modal-checkbox-group-acc"
                    : "add-product-modal-checkbox-group"
                  }`}
              >
                <label htmlFor="isNewDevice">New Device</label>
                <input
                  type="checkbox"
                  id="isNewDevice"
                  name="isNewDevice"
                  checked={formData.isNewDevice}
                  onChange={handleInputChange}
                />
              </div>
              <div
                className={`${isPartsURL
                  ? "add-product-modal-checkbox-group-used-parts"
                  : isAccessoriesURL
                    ? "add-product-modal-checkbox-group-used-acc"
                    : "add-product-modal-checkbox-group-used"
                  }`}
              >
                <label htmlFor="isUsedDevice">Used Device</label>
                <input
                  type="checkbox"
                  id="isUsedDevice"
                  name="isUsedDevice"
                  checked={formData.isUsedDevice}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          {!isLoycomDomain && (
            <div className="edit-product-modal-pricing">
              <span className="edit-product-modal-pricing-label">Pricing</span>
              <div className="edit-product-modal-pricing-row">
                <div className="edit-product-modal-pricing-col">
                  <span className="edit-product-modal-pricing-sublabel">Avg. Buying Price</span>
                  <div className="edit-product-modal-input-disabled">
                    <span className="edit-product-modal-input-text">
                      € {parseFloat(product?.avg_buying_price || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="edit-product-modal-pricing-col">
                  <span className="edit-product-modal-pricing-sublabel">Selling Price *</span>
                  <input
                    type="number"
                    className="edit-product-modal-selling-input"
                    name="sellingPrice"
                    value={formData.sellingPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="edit-product-modal-variant-features">
            <div className="edit-product-modal-variant-header">
              <h3>Variant Features</h3>
              <button
                type="button"
                className="edit-product-modal-add-variant-btn"
                onClick={handleAddVariant}
              >
                <Plus /> Add Variant
              </button>
            </div>
            <div
              className={`${isPartsURL
                ? "variant-inputs-parts"
                : isAccessoriesURL
                  ? "variant-inputs-acc"
                  : "variant-inputs"
                }`}
            >
              <input
                type="text"
                placeholder="Feature name (e.g. Color)"
                value={variantFeature.name}
                onChange={(e) =>
                  setVariantFeature((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
              <input
                type="text"
                placeholder="Feature value (e.g. Black)"
                value={variantFeature.value}
                onChange={(e) =>
                  setVariantFeature((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
              />
              {editingVariantIndex !== null && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="edit-product-modal-cancel-edit-btn"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Display added variants */}
            {formData.variantFeatures.length > 0 && (
              <div className="edit-product-modal-added-variants">
                <h4>Added Variants:</h4>
                {formData.variantFeatures.map((variant, index) => (
                  <div key={index} className="edit-product-modal-variant-item">
                    <span>
                      {variant.name}: {variant.value}
                    </span>
                    <div className="edit-product-modal-variant-actions">
                      <button
                        type="button"
                        onClick={() => handleEditVariant(index)}
                        className={`${isPartsURL
                          ? "edit-variant-btn-parts"
                          : isAccessoriesURL
                            ? "edit-variant-btn-acc"
                            : isGodownURL
                              ? "edit-variant-btn-godown"
                              : "edit-variant-btn"
                          }`}
                        disabled={editingVariantIndex !== null}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveVariant(index)}
                        className="edit-product-modal-remove-variant-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="edit-product-modal-actions">
            <button
              type="button"
              className="edit-product-modal-cancel-btn"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${isPartsURL
                ? "submit-btn-parts"
                : isAccessoriesURL
                  ? "submit-btn-acc"
                  : isGodownURL
                    ? "submit-btn-godown"
                  : "submit-btn"
                }`}
            >
              Update Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
